import React, { useState } from 'react';
/**
 * Contact App - Interactive Contact Experience
 * Multi-view contact system with social showcase
 */
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Send, Linkedin, Github, Twitter, MessageSquare, Users, Info, MapPin, Clock, CheckCircle2, ExternalLink, Sparkles } from 'lucide-react';
import { useTheme } from '../../core/contexts/ThemeContext';
type ViewMode = 'contact' | 'social' | 'about';
export function ContactApp() {
  const {
    theme: currentTheme
  } = useTheme();
  const [activeView, setActiveView] = useState<ViewMode>('contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const isLightTheme = currentTheme === 'light';
  const isNeonTheme = currentTheme === 'neon';
  const isHighContrast = currentTheme === 'highContrast';
  const views = [{
    id: 'contact' as const,
    label: 'Contact',
    icon: Mail
  }, {
    id: 'social' as const,
    label: 'Social',
    icon: Users
  }, {
    id: 'about' as const,
    label: 'Info',
    icon: Info
  }];
  const socialLinks = [{
    id: 'linkedin',
    icon: Linkedin,
    label: 'LinkedIn',
    handle: '@mick-van-latum',
    href: 'https://www.linkedin.com/in/mick-van-latum',
    color: 'from-blue-500 to-blue-600',
    description: 'Professional network',
    available: true
  }, {
    id: 'github',
    icon: Github,
    label: 'GitHub',
    handle: '@mvlcreative',
    href: '#',
    color: 'from-slate-600 to-slate-700',
    description: 'Binnenkort beschikbaar',
    available: false
  }, {
    id: 'x',
    icon: Twitter,
    label: 'X (Twitter)',
    handle: '@mvlcreative',
    href: '#',
    color: 'from-slate-800 to-slate-900',
    description: 'Binnenkort beschikbaar',
    available: false
  }];
  const contactMethods = [{
    icon: Mail,
    label: 'Email',
    value: 'Mick@mvlcreative.com',
    href: 'mailto:Mick@mvlcreative.com',
    primary: true
  }, {
    icon: MessageSquare,
    label: 'Telefoon',
    value: '0651244475',
    href: 'tel:+31651244475',
    primary: false
  }, {
    icon: MapPin,
    label: 'Locatie',
    value: 'Almere, Netherlands',
    href: null,
    primary: false
  }];
  const availability = [{
    label: 'Freelance Projects',
    available: true
  }, {
    label: 'Consultancy',
    available: true
  }, {
    label: 'Workshops',
    available: true
  }, {
    label: 'Full-time',
    available: false
  }];
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');
    setTimeout(() => {
      setFormStatus('sent');
      setTimeout(() => {
        setFormStatus('idle');
        setFormData({
          name: '',
          email: '',
          message: ''
        });
      }, 2000);
    }, 1500);
  };
  return <div className={`h-full flex flex-col ${isLightTheme ? 'bg-gradient-to-br from-white to-gray-100' : isHighContrast ? 'bg-black' : isNeonTheme ? 'bg-black' : 'bg-gradient-to-br from-gray-900 to-black'}`} data-id="element-71">
      {/* Toolbar - Optimized spacing */}
      <div className={`flex items-center justify-between px-4 md:px-6 py-3 backdrop-blur-xl border-b ${isLightTheme ? 'bg-white/95 border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-black/40 border-white/10'}`} data-id="element-72">
        {/* Left - Title */}
        <div className="flex items-center gap-3" data-id="element-73">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center ${isNeonTheme ? 'shadow-lg shadow-amber-500/50' : ''}`} data-id="element-74">
            <Mail className="w-5 h-5 text-white" data-id="element-75" />
          </div>
          <div className="hidden sm:block" data-id="element-76">
            <div className={`text-base font-bold ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-77">
              Contact
            </div>
            <div className={`text-xs ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/40'}`} data-id="element-78">
              Laten we verbinden
            </div>
          </div>
        </div>
        {/* Center - View Selector (Desktop) */}
        <div className={`hidden md:flex items-center gap-2 rounded-xl p-1 ${isLightTheme ? 'bg-gray-100' : isHighContrast ? 'bg-gray-100' : isNeonTheme ? 'bg-fuchsia-900/20' : 'bg-white/5'}`} data-id="element-79">
          {views.map(view => {
          const Icon = view.icon;
          const isActive = activeView === view.id;
          return <button key={view.id} onClick={() => setActiveView(view.id)} className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all ${isActive ? 'text-white' : isLightTheme ? 'text-gray-600 hover:text-gray-900' : isHighContrast ? 'text-black hover:text-gray-700' : isNeonTheme ? 'text-cyan-300 hover:text-cyan-100' : 'text-white/50 hover:text-white/80'}`} data-id="element-80">
                {isActive && <motion.div layoutId="activeContactView" className={`absolute inset-0 rounded-lg ${isHighContrast ? 'bg-black' : isNeonTheme ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-500 shadow-lg shadow-fuchsia-500/50' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`} transition={{
              type: 'spring',
              bounce: 0.2,
              duration: 0.6
            }} data-id="element-81" />}
                <div className="relative z-10 flex items-center gap-2" data-id="element-82">
                  <Icon className="w-4 h-4" data-id="element-83" />
                  {view.label}
                </div>
              </button>;
        })}
        </div>
        {/* Right - Status Badge */}
        <div className={`hidden lg:flex items-center gap-2 px-3 py-1.5 rounded-lg ${isLightTheme ? 'bg-emerald-50 border border-emerald-200' : isHighContrast ? 'bg-white border-2 border-black' : isNeonTheme ? 'bg-emerald-900/20 border border-emerald-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`} data-id="element-84">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-85" />
          <span className={`text-sm font-medium ${isLightTheme ? 'text-emerald-700' : isHighContrast ? 'text-black' : isNeonTheme ? 'text-emerald-300' : 'text-emerald-400'}`} data-id="element-86">
            Available
          </span>
        </div>
      </div>
      {/* Mobile View Selector - Consistent with other apps */}
      <div className={`md:hidden flex items-center gap-2 px-4 py-2.5 border-b overflow-x-auto ${isLightTheme ? 'bg-gray-100 border-gray-200' : isHighContrast ? 'bg-gray-100 border-black' : isNeonTheme ? 'bg-black border-fuchsia-500/30' : 'bg-black/20 border-white/10'}`} data-id="element-87">
        {views.map(view => {
        const Icon = view.icon;
        const isActive = activeView === view.id;
        return <button key={view.id} onClick={() => setActiveView(view.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${isActive ? isHighContrast ? 'bg-black text-white' : isNeonTheme ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/50' : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' : isLightTheme ? 'bg-gray-200 text-gray-600' : isHighContrast ? 'bg-white text-black border border-black' : isNeonTheme ? 'bg-fuchsia-900/20 text-cyan-300 border border-cyan-500/30' : 'bg-white/5 text-white/50'}`} data-id="element-88">
              <Icon className="w-4 h-4" data-id="element-89" />
              {view.label}
            </button>;
      })}
      </div>
      {/* Main Content - Better padding consistency */}
      <div className="flex-1 overflow-y-auto p-6 md:p-8" data-id="element-90">
        <AnimatePresence mode="wait" data-id="element-91">
          <motion.div key={activeView} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="max-w-4xl mx-auto" data-id="element-92">
            {/* Contact View */}
            {activeView === 'contact' && <div className="grid md:grid-cols-2 gap-6" data-id="element-93">
                {/* Contact Form - DISABLED */}
                <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} className={`backdrop-blur-xl rounded-2xl p-6 border relative overflow-hidden ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-94">
                  {/* Disabled Overlay */}
                  <div className="absolute inset-0 bg-black/5 backdrop-blur-[2px] z-10 flex items-center justify-center" data-id="element-95">
                    <div className={`px-4 py-2 rounded-lg ${isLightTheme ? 'bg-gray-100 border border-gray-300' : isHighContrast ? 'bg-white border-2 border-black' : isNeonTheme ? 'bg-fuchsia-900/20 border border-fuchsia-500/50' : 'bg-white/10 border border-white/20'}`} data-id="element-96">
                      <p className={`text-sm font-medium ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-300' : 'text-white/80'}`} data-id="element-97">
                        📧 Tijdelijk niet beschikbaar
                      </p>
                      <p className={`text-xs mt-1 ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400/60' : 'text-white/50'}`} data-id="element-98">
                        Gebruik email of telefoon
                      </p>
                    </div>
                  </div>
                  <h2 className={`text-xl font-bold mb-6 grayscale ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-99">
                    Stuur een bericht
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-4 grayscale" data-id="element-100">
                    <div data-id="element-101">
                      <label className={`block text-sm font-medium mb-2 ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-300' : 'text-white/80'}`} data-id="element-102">
                        Naam
                      </label>
                      <input type="text" value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} className={`w-full px-4 py-3 rounded-lg transition-colors focus:outline-none ${isLightTheme ? 'bg-gray-50 border border-gray-300 text-gray-900 focus:border-amber-500' : isHighContrast ? 'bg-white border-2 border-black text-black focus:border-gray-700' : isNeonTheme ? 'bg-fuchsia-900/20 border border-cyan-500/30 text-cyan-100 focus:border-cyan-500' : 'bg-white/5 border border-white/10 text-white focus:border-amber-500'}`} disabled data-id="element-103" />
                    </div>
                    <div data-id="element-104">
                      <label className={`block text-sm font-medium mb-2 ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-300' : 'text-white/80'}`} data-id="element-105">
                        Email
                      </label>
                      <input type="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} className={`w-full px-4 py-3 rounded-lg transition-colors focus:outline-none ${isLightTheme ? 'bg-gray-50 border border-gray-300 text-gray-900 focus:border-amber-500' : isHighContrast ? 'bg-white border-2 border-black text-black focus:border-gray-700' : isNeonTheme ? 'bg-fuchsia-900/20 border border-cyan-500/30 text-cyan-100 focus:border-cyan-500' : 'bg-white/5 border border-white/10 text-white focus:border-amber-500'}`} disabled data-id="element-106" />
                    </div>
                    <div data-id="element-107">
                      <label className={`block text-sm font-medium mb-2 ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-300' : 'text-white/80'}`} data-id="element-108">
                        Bericht
                      </label>
                      <textarea value={formData.message} onChange={e => setFormData({
                    ...formData,
                    message: e.target.value
                  })} rows={5} className={`w-full px-4 py-3 rounded-lg transition-colors focus:outline-none resize-none ${isLightTheme ? 'bg-gray-50 border border-gray-300 text-gray-900 focus:border-amber-500' : isHighContrast ? 'bg-white border-2 border-black text-black focus:border-gray-700' : isNeonTheme ? 'bg-fuchsia-900/20 border border-cyan-500/30 text-cyan-100 focus:border-cyan-500' : 'bg-white/5 border border-white/10 text-white focus:border-amber-500'}`} disabled data-id="element-109" />
                    </div>
                    <button type="submit" disabled className={`w-full px-6 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 opacity-50 cursor-not-allowed ${isHighContrast ? 'bg-black text-white' : isNeonTheme ? 'bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white' : 'bg-gradient-to-r from-amber-500 to-orange-500 text-white'}`} data-id="element-110">
                      Verstuur
                      <Send className="w-4 h-4" data-id="element-111" />
                    </button>
                  </form>
                </motion.div>
                {/* Contact Methods */}
                <div className="space-y-4" data-id="element-112">
                  {contactMethods.map((method, index) => {
                const Icon = method.icon;
                return <motion.a key={method.label} href={method.href || undefined} target={method.href?.startsWith('http') ? '_blank' : undefined} rel={method.href?.startsWith('http') ? 'noopener noreferrer' : undefined} initial={{
                  opacity: 0,
                  x: 20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.1
                }} whileHover={method.href ? {
                  scale: 1.02
                } : undefined} className={`backdrop-blur-xl rounded-2xl p-6 border block ${method.href ? 'cursor-pointer' : 'cursor-default'} ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-113">
                        <div className="flex items-center gap-4" data-id="element-114">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0 ${isNeonTheme ? 'shadow-lg shadow-amber-500/50' : ''}`} data-id="element-115">
                            <Icon className="w-6 h-6 text-white" data-id="element-116" />
                          </div>
                          <div className="flex-1" data-id="element-117">
                            <div className={`text-sm font-medium mb-1 ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-118">
                              {method.label}
                            </div>
                            <div className={`font-bold ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-119">
                              {method.value}
                            </div>
                          </div>
                          {method.primary && <div className={`px-2 py-1 rounded text-xs font-medium ${isLightTheme ? 'bg-amber-100 text-amber-700' : isHighContrast ? 'bg-black text-white' : isNeonTheme ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-500/20 text-amber-400'}`} data-id="element-120">
                              Primary
                            </div>}
                        </div>
                      </motion.a>;
              })}
                </div>
              </div>}
            {/* Social View */}
            {activeView === 'social' && <div className="space-y-6" data-id="element-121">
                <div className="text-center mb-8" data-id="element-122">
                  <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-123">
                    Connect Online
                  </h2>
                  <p className={`${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-300' : 'text-white/60'}`} data-id="element-124">
                    Volg mijn werk en blijf op de hoogte
                  </p>
                </div>
                <div className="grid md:grid-cols-3 gap-6" data-id="element-125">
                  {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return <motion.a key={social.id} href={social.available ? social.href : undefined} target={social.available ? '_blank' : undefined} rel={social.available ? 'noopener noreferrer' : undefined} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: index * 0.1
                }} whileHover={social.available ? {
                  y: -8,
                  scale: 1.02
                } : undefined} className={`backdrop-blur-xl rounded-2xl p-6 border text-center group relative ${!social.available ? 'grayscale opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-126">
                        {!social.available && <div className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium bg-gray-500/20 text-gray-400" data-id="element-127">
                            Soon
                          </div>}
                        <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${social.color} flex items-center justify-center mx-auto mb-4 ${social.available ? 'group-hover:scale-110' : ''} transition-transform ${isNeonTheme && social.available ? 'shadow-lg shadow-fuchsia-500/50' : ''}`} data-id="element-128">
                          <Icon className="w-8 h-8 text-white" data-id="element-129" />
                        </div>
                        <h3 className={`text-lg font-bold mb-1 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-130">
                          {social.label}
                        </h3>
                        <p className={`text-sm mb-2 ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-700' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-131">
                          {social.handle}
                        </p>
                        <p className={`text-xs ${isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-600' : isNeonTheme ? 'text-cyan-300' : 'text-white/40'}`} data-id="element-132">
                          {social.description}
                        </p>
                        {social.available && <div className="flex items-center justify-center gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-133">
                            <ExternalLink className={`w-4 h-4 ${isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-400' : 'text-white/60'}`} data-id="element-134" />
                            <span className={`text-sm ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-black' : isNeonTheme ? 'text-cyan-300' : 'text-white/60'}`} data-id="element-135">
                              Visit Profile
                            </span>
                          </div>}
                      </motion.a>;
              })}
                </div>
              </div>}
            {/* About View */}
            {activeView === 'about' && <div className="space-y-6" data-id="element-136">
                <div className="text-center mb-8" data-id="element-137">
                  <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-138">
                    Beschikbaarheid & Info
                  </h2>
                  <p className={`${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-300' : 'text-white/60'}`} data-id="element-139">
                    Wat je moet weten over samenwerken
                  </p>
                </div>
                {/* Availability Status */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} className={`backdrop-blur-xl rounded-2xl p-6 border ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-140">
                  <h3 className={`text-xl font-bold mb-4 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-141">
                    Beschikbaar voor
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4" data-id="element-142">
                    {availability.map(item => <div key={item.label} className={`flex items-center gap-3 p-4 rounded-xl ${isLightTheme ? 'bg-gray-50' : isHighContrast ? 'bg-gray-100' : isNeonTheme ? 'bg-fuchsia-900/20' : 'bg-white/5'}`} data-id="element-143">
                        {item.available ? <CheckCircle2 className={`w-5 h-5 ${isHighContrast ? 'text-black' : isNeonTheme ? 'text-emerald-400' : 'text-emerald-500'}`} data-id="element-144" /> : <Clock className={`w-5 h-5 ${isLightTheme ? 'text-gray-400' : isHighContrast ? 'text-gray-500' : 'text-white/30'}`} data-id="element-145" />}
                        <span className={`font-medium ${item.available ? isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white' : isLightTheme ? 'text-gray-500' : isHighContrast ? 'text-gray-600' : 'text-white/40'}`} data-id="element-146">
                          {item.label}
                        </span>
                      </div>)}
                  </div>
                </motion.div>
                {/* Response Time */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className={`backdrop-blur-xl rounded-2xl p-6 border ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-147">
                  <h3 className={`text-xl font-bold mb-4 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-148">
                    Response Time
                  </h3>
                  <div className="flex items-center gap-4" data-id="element-149">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center ${isNeonTheme ? 'shadow-lg shadow-emerald-500/50' : ''}`} data-id="element-150">
                      <Clock className="w-8 h-8 text-white" data-id="element-151" />
                    </div>
                    <div data-id="element-152">
                      <div className={`text-2xl font-bold ${isHighContrast ? 'text-black' : isNeonTheme ? 'text-emerald-400' : 'text-emerald-500'}`} data-id="element-153">
                        24-48 uur
                      </div>
                      <p className={`text-sm ${isLightTheme ? 'text-gray-600' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-300' : 'text-white/60'}`} data-id="element-154">
                        Gemiddelde reactietijd
                      </p>
                    </div>
                  </div>
                </motion.div>
                {/* Work Philosophy */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className={`backdrop-blur-xl rounded-2xl p-6 border ${isLightTheme ? 'bg-white border-gray-200' : isHighContrast ? 'bg-white border-4 border-black' : isNeonTheme ? 'bg-black/90 border-fuchsia-500/50' : 'bg-white/5 border-white/10'}`} data-id="element-155">
                  <h3 className={`text-xl font-bold mb-4 ${isLightTheme ? 'text-gray-900' : isHighContrast ? 'text-black' : 'text-white'}`} data-id="element-156">
                    Werkwijze
                  </h3>
                  <p className={`leading-relaxed ${isLightTheme ? 'text-gray-700' : isHighContrast ? 'text-gray-900' : isNeonTheme ? 'text-cyan-200' : 'text-white/80'}`} data-id="element-157">
                    Ik werk graag in korte, iteratieve sprints waarbij we snel
                    tot werkende prototypes komen. Door AI-tools slim in te
                    zetten kan ik complexe concepten snel visualiseren en
                    testen. Perfect voor ondernemers die snel willen valideren
                    of organisaties die innovatie zoeken.
                  </p>
                </motion.div>
              </div>}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}